/**
 * Unified Media Cache Service
 * Global singleton service to manage all media preloading and caching
 */

'use client';

import { getMediaTypeFromUrl, extractFallbackUrls, resolveResponsiveUrl, isMobileDevice } from './video-utils';
import { mediaConfig } from '@/config/media-config';
import { ResponsiveBackgroundImage } from '@/types/sections';
import { getCanonicalMediaUrl } from './url-normalization';
import { persistentMediaCache, type CacheMetadata } from './persistent-media-cache';
import { getMediaFromIndexedDB, hasMediaInIndexedDB, storeMediaInIndexedDB } from './indexeddb-media-cache';

export type MediaType = 'video' | 'image' | 'unknown';

export interface CachedMediaItem {
  url: string;
  type: MediaType;
  cached: boolean;
  timestamp: number;
  element?: HTMLImageElement | HTMLVideoElement;
}

class MediaCacheService {
  private memoryCache = new Map<string, CachedMediaItem>();
  private preloadQueue: string[] = [];
  private preloading = new Set<string>();
  private preloaded = new Set<string>();
  private browserCached = new Set<string>();
  private cacheApi: Cache | null = null;
  private cacheName = 'evoque-media-cache-v2';
  private syncInterval: NodeJS.Timeout | null = null;
  private initialized = false;

  constructor() {
    if (typeof window !== 'undefined') {
      this.initialize();
    }
  }

  /**
   * Initialize cache with persistence
   */
  private async initialize() {
    if (this.initialized) return;
    this.initialized = true;

    // Initialize IndexedDB
    await persistentMediaCache.initialize();

    // Restore cache state from localStorage
    this.restoreCacheState();

    // Initialize Cache API
    await this.initializeCacheAPI();

    // Start periodic sync (every 5 minutes)
    this.startPeriodicSync();
  }

  /**
   * Restore cache state from localStorage
   */
  private restoreCacheState() {
    const metadata = persistentMediaCache.loadCacheState();
    
    for (const item of metadata) {
      const cacheKey = this.getCacheKey(item.url);
      
      // Restore to memory cache
      this.memoryCache.set(cacheKey, {
        url: item.url,
        type: item.type,
        cached: item.cached,
        timestamp: item.timestamp,
      });

      // Mark as preloaded if cached
      if (item.cached) {
        this.preloaded.add(cacheKey);
        this.browserCached.add(item.url);
      }
    }

    if (process.env.NODE_ENV === 'development' && metadata.length > 0) {
      console.log(`[MediaCache] Restored ${metadata.length} items from localStorage`);
    }
  }

  /**
   * Save cache state to localStorage
   */
  private saveCacheState() {
    const metadata: CacheMetadata[] = [];
    
    for (const [key, item] of this.memoryCache.entries()) {
      metadata.push({
        url: item.url,
        type: item.type,
        timestamp: item.timestamp,
        cached: item.cached,
      });
    }

    persistentMediaCache.saveCacheState(metadata);
  }

  /**
   * Start periodic sync to localStorage
   */
  private startPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    // Sync every 5 minutes
    this.syncInterval = setInterval(() => {
      this.saveCacheState();
    }, 5 * 60 * 1000);
  }

  private async initializeCacheAPI() {
    if ('caches' in window) {
      try {
        this.cacheApi = await caches.open(this.cacheName);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[MediaCache] Failed to open cache API:', error);
        }
      }
    }
  }

  /**
   * Normalize URL - convert /api/v1/media/download to /api/media/download
   * Also extracts S3 key to create canonical URL for cache deduplication
   * This prevents duplicate downloads when URLs have different query parameters
   * Uses getCanonicalMediaUrl for consistent normalization across the codebase
   */
  private normalizeUrl(url: string): string {
    const canonical = getCanonicalMediaUrl(url);
    return canonical || url;
  }

  /**
   * Get cache key from URL
   * Uses normalized URL to ensure URLs with different query params map to same cache key
   */
  private getCacheKey(url: string): string {
    return this.normalizeUrl(url);
  }

  /**
   * Resolve responsive URL to actual URL string
   */
  private resolveUrl(url: string | ResponsiveBackgroundImage): string | undefined {
    if (typeof url === 'string') {
      return url;
    }
    const isMobile = isMobileDevice();
    return resolveResponsiveUrl(url, isMobile);
  }

  /**
   * Check if media is in browser cache
   * Checks: localStorage state → IndexedDB → memory cache → Cache API → browser cache
   */
  async isInBrowserCache(url: string | ResponsiveBackgroundImage): Promise<boolean> {
    const resolvedUrl = this.resolveUrl(url);
    if (!resolvedUrl) return false;
    
    const normalizedUrl = this.normalizeUrl(resolvedUrl);
    const cacheKey = this.getCacheKey(normalizedUrl);

    // Check memory cache first (fastest)
    if (this.browserCached.has(normalizedUrl)) {
      return true;
    }

    const cachedItem = this.memoryCache.get(cacheKey);
    if (cachedItem?.cached) {
      return true;
    }

    // Check IndexedDB for large files
    const hasInIndexedDB = await hasMediaInIndexedDB(normalizedUrl);
    if (hasInIndexedDB) {
      this.browserCached.add(normalizedUrl);
      this.preloaded.add(cacheKey);
      this.memoryCache.set(cacheKey, {
        url: normalizedUrl,
        type: getMediaTypeFromUrl(normalizedUrl) || 'unknown',
        cached: true,
        timestamp: Date.now(),
      });
      return true;
    }

    // Check browser cache using Cache API
    if (this.cacheApi) {
      try {
        const cached = await this.cacheApi.match(normalizedUrl);
        if (cached) {
          this.browserCached.add(normalizedUrl);
          return true;
        }
      } catch (error) {
        // Silently fail
      }
    }

    // Quick browser cache check using image/video element
    return this.checkBrowserCacheQuick(normalizedUrl);
  }

  /**
   * Quick browser cache check using media elements
   */
  private async checkBrowserCacheQuick(url: string): Promise<boolean> {
    const mediaType = getMediaTypeFromUrl(url) || 'image';

    if (mediaType === 'image') {
      return new Promise<boolean>((resolve) => {
        const img = new Image();
        let resolved = false;

        const handleLoad = () => {
          if (!resolved) {
            resolved = true;
            this.browserCached.add(url);
            resolve(true);
          }
        };

        const handleError = () => {
          if (!resolved) {
            resolved = true;
            resolve(false);
          }
        };

        img.onload = handleLoad;
        img.onerror = handleError;
        img.src = url;

        // Timeout after 50ms - cached images load instantly
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            if (img.complete && img.naturalWidth > 0) {
              this.browserCached.add(url);
              resolve(true);
            } else {
              resolve(false);
            }
          }
        }, 50);
      });
    } else if (mediaType === 'video') {
      return new Promise<boolean>((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        let resolved = false;

        const handleLoad = () => {
          if (!resolved) {
            resolved = true;
            this.browserCached.add(url);
            video.remove();
            resolve(true);
          }
        };

        const handleError = () => {
          if (!resolved) {
            resolved = true;
            video.remove();
            resolve(false);
          }
        };

        video.onloadedmetadata = handleLoad;
        video.onerror = handleError;
        video.src = url;

        // Timeout after 100ms - cached videos load quickly
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            if (video.readyState >= 1) {
              this.browserCached.add(url);
              resolve(true);
            } else {
              resolve(false);
            }
            video.remove();
          }
        }, 100);
      });
    }

    return false;
  }

  /**
   * Preload a single media item
   * Supports both string URLs and responsive video URLs
   */
  async preloadMedia(url: string | ResponsiveBackgroundImage, priority: 'high' | 'low' = 'low'): Promise<boolean> {
    const resolvedUrl = this.resolveUrl(url);
    if (!resolvedUrl) return false;
    
    const normalizedUrl = this.normalizeUrl(resolvedUrl);
    const cacheKey = this.getCacheKey(normalizedUrl);

    // Skip if already preloaded
    if (this.preloaded.has(cacheKey)) {
      return true;
    }

    // Skip if currently preloading
    if (this.preloading.has(cacheKey)) {
      return false;
    }

    // Check if already cached
    const isCached = await this.isInBrowserCache(normalizedUrl);
    if (isCached) {
      this.preloaded.add(cacheKey);
      this.memoryCache.set(cacheKey, {
        url: normalizedUrl,
        type: getMediaTypeFromUrl(normalizedUrl) || 'unknown',
        cached: true,
        timestamp: Date.now(),
      });
      return true;
    }

    this.preloading.add(cacheKey);

    try {
      const mediaType = getMediaTypeFromUrl(normalizedUrl) || 'image';

      // Check IndexedDB first for large files
      const hasInIndexedDB = await hasMediaInIndexedDB(normalizedUrl);
      if (hasInIndexedDB) {
        this.preloaded.add(cacheKey);
        this.preloading.delete(cacheKey);
        this.browserCached.add(normalizedUrl);
        this.memoryCache.set(cacheKey, {
          url: normalizedUrl,
          type: mediaType,
          cached: true,
          timestamp: Date.now(),
        });
        this.saveCacheState(); // Sync to localStorage
        return true;
      }

      // Preload media
      if (mediaType === 'image') {
        await this.preloadImage(normalizedUrl);
      } else if (mediaType === 'video') {
        await this.preloadVideo(normalizedUrl);
      }

      // For large files, also store in IndexedDB
      // We need to fetch the response to get the size
      try {
        const response = await fetch(normalizedUrl, { method: 'HEAD' });
        const contentLength = response.headers.get('content-length');
        if (contentLength) {
          const size = parseInt(contentLength, 10);
          if (persistentMediaCache.shouldUseIndexedDB(size, normalizedUrl)) {
            // Fetch full response and store in IndexedDB
            const fullResponse = await fetch(normalizedUrl);
            await storeMediaInIndexedDB(normalizedUrl, fullResponse);
          }
        }
      } catch (error) {
        // Silently fail - IndexedDB storage is optional
      }

      this.preloaded.add(cacheKey);
      this.preloading.delete(cacheKey);
      this.browserCached.add(normalizedUrl);

      this.memoryCache.set(cacheKey, {
        url: normalizedUrl,
        type: mediaType,
        cached: true,
        timestamp: Date.now(),
      });

      // Sync to localStorage
      this.saveCacheState();

      return true;
    } catch (error) {
      this.preloading.delete(cacheKey);
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[MediaCache] Failed to preload ${normalizedUrl}:`, error);
      }
      return false;
    }
  }

  /**
   * Preload image
   */
  private async preloadImage(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        this.browserCached.add(url);
        resolve();
      };

      img.onerror = () => {
        reject(new Error(`Failed to load image: ${url}`));
      };

      img.src = url;

      // Timeout after 30 seconds
      setTimeout(() => {
        if (!img.complete) {
          reject(new Error(`Image load timeout: ${url}`));
        }
      }, 30000);
    });
  }

  /**
   * Preload video (metadata only to save bandwidth)
   */
  private async preloadVideo(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.style.display = 'none';
      video.style.position = 'absolute';
      video.style.width = '1px';
      video.style.height = '1px';
      video.style.opacity = '0';
      video.style.pointerEvents = 'none';
      video.style.zIndex = '-9999';

      document.body.appendChild(video);

      const cleanup = () => {
        try {
          video.remove();
        } catch (e) {
          // Ignore cleanup errors
        }
      };

      const handleLoadedMetadata = () => {
        this.browserCached.add(url);
        cleanup();
        resolve();
      };

      const handleError = () => {
        cleanup();
        reject(new Error(`Failed to load video: ${url}`));
      };

      video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
      video.addEventListener('error', handleError, { once: true });

      video.src = url;

      // Timeout after 30 seconds
      setTimeout(() => {
        if (video.readyState < 1) {
          cleanup();
          reject(new Error(`Video load timeout: ${url}`));
        }
      }, 30000);
    });
  }

  /**
   * Preload video with fallback URLs (extracts desktop/mobile variants)
   * This ensures all variants are cached for fast switching
   * On mobile devices, prioritizes mobile URL first
   */
  async preloadVideoWithFallbacks(
    videoUrl: string | ResponsiveBackgroundImage,
    priority: 'high' | 'low' = 'low'
  ): Promise<void> {
    const isMobile = isMobileDevice();

    // Extract all fallback URLs (desktop and mobile variants)
    const fallbackUrls = extractFallbackUrls(videoUrl, isMobile);

    // Preload all variants to ensure fast switching
    // But prioritize the appropriate URL for the current device
    if (fallbackUrls.length > 0) {
      // Preload the primary URL first (mobile URL on mobile, desktop URL on desktop)
      const primaryUrl = resolveResponsiveUrl(videoUrl, isMobile);
      if (primaryUrl) {
        await this.preloadMedia(primaryUrl, priority);
      }
      
      // Then preload fallbacks in background
      const fallbackUrlsToPreload = fallbackUrls.filter(url => url !== primaryUrl);
      if (fallbackUrlsToPreload.length > 0) {
        await this.preloadBatch(fallbackUrlsToPreload, 'low');
      }
    }
  }

  /**
   * Batch preload media items with support for video fallback URLs
   */
  async preloadBatch(urls: string[], priority: 'high' | 'low' = 'low'): Promise<void> {
    const config = mediaConfig.preload;
    const batchSize = config.batchSize;

    // Process in batches
    for (let i = 0; i < urls.length; i += batchSize) {
      const batch = urls.slice(i, i + batchSize);

      // Use requestIdleCallback for low priority, immediate for high priority
      if (priority === 'low' && 'requestIdleCallback' in window) {
        await new Promise<void>((resolve) => {
          requestIdleCallback(
            async () => {
              await Promise.allSettled(
                batch.map((url) => this.preloadMedia(url, priority))
              );
              resolve();
            },
            { timeout: 5000 }
          );
        });
      } else {
        // High priority or no requestIdleCallback - load immediately
        await Promise.allSettled(
          batch.map((url) => this.preloadMedia(url, priority))
        );
      }

      // Small delay between batches to avoid overwhelming the network
      if (i + batchSize < urls.length) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }
  }

  /**
   * Check if media is preloaded
   * Supports both string URLs and responsive video URLs
   */
  isPreloaded(url: string | ResponsiveBackgroundImage): boolean {
    const resolvedUrl = this.resolveUrl(url);
    if (!resolvedUrl) return false;
    
    const normalizedUrl = this.normalizeUrl(resolvedUrl);
    const cacheKey = this.getCacheKey(normalizedUrl);
    return this.preloaded.has(cacheKey) || this.browserCached.has(normalizedUrl);
  }

  /**
   * Get cached media item
   * Supports both string URLs and responsive video URLs
   */
  getCached(url: string | ResponsiveBackgroundImage): CachedMediaItem | undefined {
    const resolvedUrl = this.resolveUrl(url);
    if (!resolvedUrl) return undefined;
    
    const normalizedUrl = this.normalizeUrl(resolvedUrl);
    const cacheKey = this.getCacheKey(normalizedUrl);
    return this.memoryCache.get(cacheKey);
  }

  /**
   * Clear cache
   */
  async clearCache(): Promise<void> {
    this.memoryCache.clear();
    this.preloaded.clear();
    this.preloading.clear();
    this.browserCached.clear();

    // Clear Cache API
    if (this.cacheApi && 'caches' in window) {
      caches.delete(this.cacheName).catch(() => {
        // Silently fail
      });
    }

    // Clear persistent cache (localStorage + IndexedDB)
    await persistentMediaCache.clearCache();
  }

  /**
   * Invalidate a specific URL from all caches
   * Call this when a 404 or error occurs to allow re-fetching
   */
  async invalidateUrl(url: string | ResponsiveBackgroundImage): Promise<void> {
    const resolvedUrl = this.resolveUrl(url);
    if (!resolvedUrl) return;

    const normalizedUrl = this.normalizeUrl(resolvedUrl);
    const cacheKey = this.getCacheKey(normalizedUrl);

    // Remove from all internal caches
    this.memoryCache.delete(cacheKey);
    this.preloaded.delete(cacheKey);
    this.preloading.delete(cacheKey);
    this.browserCached.delete(normalizedUrl);

    // Remove from Cache API
    if (this.cacheApi) {
      try {
        await this.cacheApi.delete(normalizedUrl);
      } catch (error) {
        // Silently fail
        if (process.env.NODE_ENV === 'development') {
          console.warn('[MediaCache] Failed to delete from Cache API:', error);
        }
      }
    }

    // Remove from persistent cache (localStorage + IndexedDB)
    await persistentMediaCache.invalidateUrl(normalizedUrl);

    // Sync to localStorage
    this.saveCacheState();

    if (process.env.NODE_ENV === 'development') {
      console.log('[MediaCache] Invalidated URL:', normalizedUrl);
    }
  }

  /**
   * Invalidate multiple URLs at once
   */
  async invalidateUrls(urls: (string | ResponsiveBackgroundImage)[]): Promise<void> {
    await Promise.all(urls.map(url => this.invalidateUrl(url)));
  }

  /**
   * Handle 404/error response - invalidate cache and optionally retry
   * Returns true if the URL was in cache (stale data), false if not cached
   */
  async handleNotFound(url: string | ResponsiveBackgroundImage): Promise<boolean> {
    const resolvedUrl = this.resolveUrl(url);
    if (!resolvedUrl) return false;

    const normalizedUrl = this.normalizeUrl(resolvedUrl);
    const cacheKey = this.getCacheKey(normalizedUrl);

    // Check if it was cached (stale data scenario)
    const wasCached = this.preloaded.has(cacheKey) || 
                      this.browserCached.has(normalizedUrl) || 
                      this.memoryCache.has(cacheKey);

    // Invalidate the URL
    await this.invalidateUrl(url);

    return wasCached;
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      cached: this.preloaded.size,
      preloading: this.preloading.size,
      browserCached: this.browserCached.size,
      memoryCacheSize: this.memoryCache.size,
    };
  }

  /**
   * Add URLs to preload queue
   */
  addToQueue(urls: string[]): void {
    const normalizedUrls = urls.map((url) => this.normalizeUrl(url));
    const uniqueUrls = Array.from(new Set(normalizedUrls));

    // Filter out already preloaded
    const newUrls = uniqueUrls.filter((url) => !this.isPreloaded(url));

    this.preloadQueue.push(...newUrls);
  }

  /**
   * Process preload queue
   */
  async processQueue(priority: 'high' | 'low' = 'low'): Promise<void> {
    if (this.preloadQueue.length === 0) {
      return;
    }

    const urls = [...this.preloadQueue];
    this.preloadQueue = [];

    await this.preloadBatch(urls, priority);
  }
}

// Export singleton instance
export const mediaCache = new MediaCacheService();

